import React from 'react';
import { Cross2Icon } from "@radix-ui/react-icons";
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { closeImage } from '../../store/chatSlice';
import logger from '../../utils/logger';

export function ImageModal() {
  const dispatch = useDispatch();
  const imageSrc = useSelector((state: RootState) => state.chat.openImageSrc);
  const imageType = useSelector((state: RootState) => state.chat.openImageType);
  const isOpen = !!imageSrc;
  
  // Use the type from Redux store
  const isPDF = imageType === 'pdf';
  
  logger.log('[ImageModal] Rendering:', { isOpen, isPDF, imageSrc: imageSrc?.substring(0, 50), imageType });

  const handleClose = React.useCallback(() => {
    dispatch(closeImage());
  }, [dispatch]);

  // Handle escape key and prevent scrolling
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    const handleWheel = (e: WheelEvent) => {
      // Prevent wheel events on the backdrop from propagating
      const target = e.target as HTMLElement;
      // Only prevent if not scrolling inside the PDF iframe container
      if (!target.closest('[data-pdf-container]')) {
        e.preventDefault();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('wheel', handleWheel, { passive: false });
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('wheel', handleWheel);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-in fade-in-0 duration-200"
      style={{ 
        backgroundColor: 'rgba(0, 0, 0, 0.8)', 
        backdropFilter: 'blur(4px)',
        overflow: 'hidden' // Prevent the modal backdrop itself from scrolling
      }}
      onClick={handleClose} // Close when clicking backdrop
    >
      {/* Close button */}
      <button
        onClick={handleClose}
        className="absolute right-4 top-4 z-10 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 animate-in slide-in-from-top-2 duration-300"
        style={{
          background: 'rgba(0, 0, 0, 0.6)',
          color: 'white',
          padding: '8px',
          borderRadius: '6px',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '40px',
          height: '40px'
        }}
      >
        <Cross2Icon className="h-6 w-6" />
        <span className="sr-only">Close</span>
      </button>

      {/* Image or PDF */}
      {imageSrc && (
        isPDF ? (
          <div
            data-pdf-container
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '90vw',
              height: '90vh',
              borderRadius: '8px',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            <iframe
              src={imageSrc}
              title="PDF document viewer"
              className="animate-in zoom-in-95 duration-300"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                backgroundColor: 'white',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
              }}
            />
          </div>
        ) : (
          <img
            src={imageSrc}
            alt="Full screen view"
            className="animate-in zoom-in-95 duration-300"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              borderRadius: '8px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking image
          />
        )
      )}
    </div>
  );
}